import { Container, Filter, filters, RenderTexture, Sprite, Spritesheet } from "pixi.js";
import { CharacterCreatorDefinition } from "../Constants/CharacterCreatorDefinition";
import { ColorReplaceFilter } from "pixi-filters";
import { SkinColors } from "../Data/SkinColors";
import { SkinColorsEnum } from "../Types/SkinColors";
import { GameObject } from "../../tsthree";

export interface ICharacterGeneratorSettings {
    skinColor?: SkinColorsEnum,
    desaturateHair?: boolean;
    hairTint?: number;
    hairVal?: number;
    earsVal?: number;
    eyesVal?: number;
    noseVal?: number;
    faceVal?: number;
    mouthVal?: number;
    eyebrowsVal?: number;
}

const DEFAULT_SETTINGS: ICharacterGeneratorSettings = {
    skinColor: SkinColorsEnum.Default,
    desaturateHair: false,
    hairTint: undefined,
    hairVal: 1,
    earsVal: 1,
    eyesVal: 1,
    noseVal: 1,
    faceVal: 1,
    mouthVal: 1,
    eyebrowsVal: 1,
};

export class CharacterPortraitGenerator extends GameObject {

    public settings: ICharacterGeneratorSettings = {};

    private headSprite: Sprite;
    private hairbackSprite: Sprite;
    private faceSprite: Sprite;
    private eyesSprite: Sprite;
    private pupilsSprite: Sprite;
    private noseSprite: Sprite;
    private earFrontSprite: Sprite;
    private mouthSprite: Sprite;
    private eyebrowsSprite: Sprite;
    private hairfrontSprite: Sprite;

    public update(settings: Partial<ICharacterGeneratorSettings>): void {
        const spritesheet =
            ENGINE.getPIXIResource("CharacterMakerSpritesheet") as Spritesheet;
        if(!spritesheet) {
            throw new Error("Spritesheet 'CharacterMakerSpritesheet' must be available!");
        }
        this.settings = Object.assign(this.settings, settings);

        const replaceFilters: Filter[] = [];
        if(
            typeof settings.skinColor !== "undefined" &&
            this.settings.skinColor !== SkinColorsEnum.Default
        ) {
            for(let i = 0; i < SkinColors[this.settings.skinColor].replace.length; i+=3) {
                const targetR = SkinColors[this.settings.skinColor].target[i];
                const targetG = SkinColors[this.settings.skinColor].target[i+1];
                const targetB = SkinColors[this.settings.skinColor].target[i+2];
                const replaceR = SkinColors[this.settings.skinColor].replace[i];
                const replaceG = SkinColors[this.settings.skinColor].replace[i+1];
                const replaceB = SkinColors[this.settings.skinColor].replace[i+2];
                replaceFilters.push(
                    new ColorReplaceFilter(
                        [targetR, targetG, targetB],
                        [replaceR, replaceG, replaceB],
                        0.001
                    ) as unknown as Filter
                );
            }
            this.headSprite.filters?.forEach((e) => e.destroy());
            this.headSprite.filters =
                this.faceSprite.filters =
                    this.noseSprite.filters =
                        this.eyesSprite.filters =
                            this.earFrontSprite.filters =
                                this.mouthSprite.filters =
                                    this.eyebrowsSprite.filters =
                                    replaceFilters;
        } else {
            this.headSprite.filters?.forEach((e) => e.destroy());
            this.headSprite.filters =
                this.faceSprite.filters =
                    this.noseSprite.filters =
                        this.eyesSprite.filters =
                            this.earFrontSprite.filters =
                                this.mouthSprite.filters =
                                    this.eyebrowsSprite.filters =
                                        [];
        }

        if(
            typeof this.settings.desaturateHair !== "undefined" &&
            this.settings.desaturateHair
        ) {
            const hairDesaturateFilter = new filters.ColorMatrixFilter();
            hairDesaturateFilter.desaturate();
            this.hairfrontSprite.filters = [ hairDesaturateFilter ];
            this.hairbackSprite.filters = [ hairDesaturateFilter ];
        } else if(
            typeof this.settings.hairTint !== "undefined" &&
            this.settings.hairTint !== 0
        ) {
            const hairColorFilter = new filters.ColorMatrixFilter();
            hairColorFilter.tint(
                CharacterCreatorDefinition.hairtints[this.settings.hairTint],
                true
            );
            this.hairfrontSprite.filters = [ hairColorFilter ];
            this.hairbackSprite.filters = [ hairColorFilter ];
        } else if(
            typeof this.settings.hairTint !== "undefined" &&
            this.settings.hairTint === 0
        ) {
            this.hairfrontSprite.filters = [ ];
            this.hairbackSprite.filters = [ ];
        }

        if(
            typeof this.settings.faceVal !== "undefined" &&
            this.faceSprite.texture !== spritesheet.textures[
                CharacterCreatorDefinition.faces[this.settings.faceVal]
            ]
        ) {
            this.faceSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.faces[this.settings.faceVal]
            ];
            this.faceSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.faces[this.settings.faceVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.faces[this.settings.faceVal]
                ].spriteSourceSize.y - 1,
            );
        }

        if(
            typeof this.settings.hairVal !== "undefined" &&
            (
                this.hairfrontSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.hairfront[this.settings.hairVal]
                ] ||
                this.hairbackSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.hairback[this.settings.hairVal]
                ]
            )
        ) {
            this.hairfrontSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.hairfront[this.settings.hairVal]
            ];
            this.hairbackSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.hairback[this.settings.hairVal]
            ];
            this.hairbackSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.hairback[this.settings.hairVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.hairback[this.settings.hairVal]
                ].spriteSourceSize.y - 1,
            );
            this.hairfrontSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.hairfront[this.settings.hairVal]
                    ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.hairfront[this.settings.hairVal]
                    ].spriteSourceSize.y - 1,
            );
        }

        if(
            typeof this.settings.eyesVal !== "undefined" &&
            (
                this.eyesSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.eyes[this.settings.eyesVal]
                ] ||
                this.eyesSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.pupils[this.settings.eyesVal]
                ]
            )
        ) {
            this.eyesSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.eyes[this.settings.eyesVal]
            ];
            this.pupilsSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.pupils[this.settings.eyesVal]
            ];
            this.eyesSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.eyes[this.settings.eyesVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.eyes[this.settings.eyesVal]
                ].spriteSourceSize.y - 1,
            );
            this.pupilsSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.pupils[this.settings.eyesVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.pupils[this.settings.eyesVal]
                ].spriteSourceSize.y - 1,
            );
        }

        if(
            typeof this.settings.earsVal !== "undefined" &&
            (
                this.earFrontSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.earFronts[this.settings.earsVal]
                ]
            // ||
            //     this.earsBackSprite.texture !== spritesheet.textures[
            //         CharacterCreatorDefinition.earFronts[this.settings.eyesVal]
            //     ]
            )
        ) {
            this.earFrontSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.earFronts[this.settings.earsVal]
            ];
            this.earFrontSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.earFronts[this.settings.earsVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.earFronts[this.settings.earsVal]
                ].spriteSourceSize.y - 1,
            );
        }

        if(
            typeof this.settings.mouthVal !== "undefined" &&
            (
                this.mouthSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.mouths[this.settings.mouthVal]
                ]
            )
        ) {
            this.mouthSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.mouths[this.settings.mouthVal]
            ];
            this.mouthSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.mouths[this.settings.mouthVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.mouths[this.settings.mouthVal]
                ].spriteSourceSize.y - 1,
            );
        }

        if(
            typeof this.settings.noseVal !== "undefined" &&
            (
                this.noseSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.noses[this.settings.noseVal]
                ]
            )
        ) {
            this.noseSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.noses[this.settings.noseVal]
            ];
            this.noseSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.noses[this.settings.noseVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.noses[this.settings.noseVal]
                ].spriteSourceSize.y - 1,
            );
        }

        if(
            typeof this.settings.eyebrowsVal !== "undefined" &&
            (
                this.eyebrowsSprite.texture !== spritesheet.textures[
                    CharacterCreatorDefinition.eyebrows[this.settings.eyebrowsVal]
                ]
            )
        ) {
            this.eyebrowsSprite.texture = spritesheet.textures[
                CharacterCreatorDefinition.eyebrows[this.settings.eyebrowsVal]
            ];
            this.eyebrowsSprite.position.set(
                spritesheet.data.frames[
                    CharacterCreatorDefinition.eyebrows[this.settings.eyebrowsVal]
                ].spriteSourceSize.x,
                spritesheet.data.frames[
                    CharacterCreatorDefinition.eyebrows[this.settings.eyebrowsVal]
                ].spriteSourceSize.y - 1,
            );
        }

        // this.cacheAsBitmap = false;
        // this.cacheAsBitmap = true;
    }

    constructor(
        settings: ICharacterGeneratorSettings = DEFAULT_SETTINGS
    ) {
        super("CharacterPortraitGenerator");
        this.settings = Object.assign(DEFAULT_SETTINGS, settings);

        const spritesheet =
            ENGINE.getPIXIResource("CharacterMakerSpritesheet") as Spritesheet;
        if(!spritesheet) {
            throw new Error("Spritesheet 'CharacterMakerSpritesheet' must be available!");
        }

        const headContainer = new Container();
        this.addChild(headContainer);
        const head = this.headSprite = new Sprite(
            spritesheet
                .textures["81"]
        );
        head.position.set(35, 32);
        headContainer.addChild(head);

        const hairbackContainer = new Container();
        this.addChild(hairbackContainer);
        const hairback = this.hairbackSprite = new Sprite();
        hairback.anchor.set(0, 0);
        hairbackContainer.addChild(hairback);

        const faceContainer = new Container();
        this.addChild(faceContainer);
        const face = this.faceSprite = new Sprite();
        face.anchor.set(0, 0);
        faceContainer.addChild(face);

        // face.filters = [];
        // console.log(replaceFilters);
        // face.filters = replaceFilters;

        // const skinColor = 0xc68642;
        // const colMatrix = new filters.ColorMatrixFilter();
        // face.filters = [
        //     colMatrix
        // ];
        // colMatrix.tint(skinColor, true);

        const eyesContainer = new Container();
        const eyes = this.eyesSprite = new Sprite();
        eyesContainer.addChild(eyes);
        this.addChild(eyesContainer);

        const pupils = this.pupilsSprite = new Sprite();
        this.addChild(pupils);

        const nose = this.noseSprite = new Sprite();
        this.addChild(nose);

        const earFront = this.earFrontSprite = new Sprite();
        this.addChild(earFront);

        const mouth = this.mouthSprite = new Sprite();
        this.addChild(mouth);

        const eyebrows = this.eyebrowsSprite = new Sprite();
        this.addChild(eyebrows);

        const hairfrontContainer = new Container();
        this.addChild(hairfrontContainer);
        const hairfront = this.hairfrontSprite = new Sprite();
        hairfront.anchor.set(0, 0);
        hairfrontContainer.addChild(hairfront);

        this.update(settings);
    }

    public createPortraitTexture(): RenderTexture {
        const spritesheet =
            ENGINE.getPIXIResource("CharacterMakerSpritesheet") as Spritesheet;
        if(!spritesheet) {
            throw new Error("Spritesheet 'CharacterMakerSpritesheet' must be available!");
        }

        const retVal = RenderTexture.create(
            128,
            128
        );

        ENGINE.getUIManager().getRenderer().render(
            this,
            {renderTexture: retVal}
        );

        return retVal;
    }
}